window.GIT_HASH = __GIT_HASH__; // eslint-disable-line
if (window.GIT_HASH) console.log(`Pulse loaded: ${window.GIT_HASH}`);

import "./assets/scss/redfast-stylesheet.scss";
import "./assets/scss/main.css";
import * as Sentry from "@sentry/vue";
import { createApp } from "vue";
import { createPinia } from "pinia";
import isBetween from "dayjs/plugin/isBetween";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import advancedFormat from "dayjs/plugin/advancedFormat";
import duration from "dayjs/plugin/duration";
import isToday from "dayjs/plugin/isToday";
import dayjs from "dayjs";
import router from "./router";
import App from "./App.vue";
import "@mdi/font/css/materialdesignicons.css";
import colors from "vuetify/lib/util/colors";
import { createVuetify } from "vuetify";
import {
  VApp,
  VAutocomplete,
  VBreadcrumbs,
  VBtn,
  VCard,
  VCardActions,
  VCardText,
  VCardTitle,
  VCheckbox,
  VChip,
  VChipGroup,
  VCol,
  VColorPicker,
  VDataTable,
  VDatePicker,
  VDialog,
  VDivider,
  VExpansionPanel,
  VExpansionPanelText,
  VExpansionPanelTitle,
  VExpansionPanels,
  VForm,
  VIcon,
  VLayout,
  VList,
  VListItem,
  VListItemAction,
  VListItemSubtitle,
  VListItemTitle,
  VMenu,
  VOverlay,
  VProgressCircular,
  VProgressLinear,
  VRadio,
  VRadioGroup,
  VRangeSlider,
  VRow,
  VSelect,
  VSpacer,
  VSwitch,
  VTab,
  VTable,
  VTabs,
  VTabsWindow,
  VTextarea,
  VTextField,
  VToolbar,
  VToolbarTitle,
  VTooltip,
  VWindowItem,
} from "vuetify/components";
import * as directives from "vuetify/directives";
import { aliases as mdAliases, md } from "vuetify/iconsets/md";
import { aliases as mdiAliases, mdi } from "vuetify/iconsets/mdi";
import { aliases as faAliases, fa } from "vuetify/iconsets/fa";
import { loadGoogleCharts } from "vue-google-charts";

// Read and strip auth_token before any third-party network activity below
// (Google Charts/Maps requests carry the URL as Referer; Sentry browserTracing
// and session replay capture it). Must run before the URL is observable to any
// of them, not just before it reaches the address bar.
const authTokenUrl = new URL(window.location.href);
const authToken = authTokenUrl.searchParams.get("auth_token");
if (authToken?.length) {
  localStorage.setItem("id_token", authToken);
  authTokenUrl.searchParams.delete("auth_token");
  window.history.replaceState(window.history.state, "", authTokenUrl);
}

loadGoogleCharts("current", { packages: ["corechart"] });
const script = document.createElement("script");
script.src =
  "https://maps.googleapis.com/maps/api/js?key=AIzaSyDJ5FGnjAar6bQ2SgGh07hVwpPKEttI4_A&libraries=places&loading=async";
script.async = true;
script.defer = true;
document.head.appendChild(script);

dayjs.extend(advancedFormat);
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(duration);
dayjs.extend(isBetween);
dayjs.extend(isToday);

const app = createApp(App);

if (!import.meta.env.DEV) {
  Sentry.init({
    app,
    dsn: "https://ad6c538a212940d3803350d9e81e7ebf@sentry.io/1452606",
    integrations: [Sentry.browserTracingIntegration({ router }), Sentry.replayIntegration()],
    tracesSampleRate: 1.0,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    // Sentry v11 collects cookies, headers, request bodies and user info by default;
    // these options restore the v10 privacy posture.
    dataCollection: {
      userInfo: false,
      cookies: false,
      httpHeaders: false,
      httpBodies: [],
    },
    // By default, tracePropagationTargets propagates tracing information to all outgoing requests.
    // Uncomment and customize the following if you want to limit propagation to specific targets:
    // tracePropagationTargets: ["localhost", /^https:\/\/api\.yourdomain\.com/],
  });
}

const vuetify = createVuetify({
  defaults: {
    VCheckbox: {
      color: "#3077A3",
    },
    VSwitch: {
      color: "#3077A3",
    },
    VRadio: {
      color: "#3077A3",
    },
    VRadioGroup: {
      color: "#3077A3",
    },
  },
  theme: {
    themes: {
      light: {
        colors: {
          "surface-light": "#FFFFFF",
          "primary": "#3096ED",
          "secondary": colors.grey.darken4, // #212121
          "accent": "#1565c0",
          "error": "#eb5660",
          "warning": "#fc8c09",
          "info": "#607d8b",
          "success": "#4caf50",
        },
      },
    },
  },
  icons: {
    defaultSet: "mdi",
    faAliases,
    mdAliases,
    mdiAliases,
    sets: { md, mdi, fa },
  },
  components: {
    VApp,
    VAutocomplete,
    VBreadcrumbs,
    VBtn,
    VCard,
    VCardActions,
    VCardText,
    VCardTitle,
    VCheckbox,
    VChip,
    VChipGroup,
    VCol,
    VColorPicker,
    VDataTable,
    VDatePicker,
    VDialog,
    VDivider,
    VExpansionPanel,
    VExpansionPanelText,
    VExpansionPanelTitle,
    VExpansionPanels,
    VForm,
    VIcon,
    VLayout,
    VList,
    VListItem,
    VListItemAction,
    VListItemSubtitle,
    VListItemTitle,
    VMenu,
    VOverlay,
    VProgressCircular,
    VProgressLinear,
    VRadio,
    VRadioGroup,
    VRangeSlider,
    VRow,
    VSelect,
    VSpacer,
    VSwitch,
    VTab,
    VTable,
    VTabs,
    VTabsWindow,
    VTextarea,
    VTextField,
    VToolbar,
    VToolbarTitle,
    VTooltip,
    VWindowItem,
  },
  directives,
});

app.use(createPinia()).use(router).use(vuetify).mount("#app");
